import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { DemoSlot } from '../demo-slot/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { resolveUrl } from '@shared/lib/url'
import { useDayjs } from '@shared/lib/format'
import { useAdminRoutes } from '@shared/lib/routes'
import { AppRoute } from '@shared/config'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'

import { useSlotsTrackerApi } from '../../api/demo-slots/slots-tracker.hook'
import { useDemoShiftControlStore } from './store'

interface TableSlotsTrackerReturn {
  slots: ComputedRef<DemoSlot[]>
  isLoading: ComputedRef<boolean>
  hasSlots: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  formatTime: (value: string | null) => string
  isCheckInLate: (slot: DemoSlot) => boolean
  getTutorHref: (tutorId: number) => string
  getBookingHref: (disciplineId: number, bookingId: number) => string
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
  isShowStatusBadge: (slot: DemoSlot) => boolean
}

export const useTableSlotsTracker = (): TableSlotsTrackerReturn => {
  const router = useRouter()
  const route = useRoute()
  const demoShiftControlStore = useDemoShiftControlStore()
  const dayjs = useDayjs()

  const slots = computed(() => demoShiftControlStore.getSlots)
  const isLoading = computed(() => demoShiftControlStore.isLoading)
  const hasSlots = computed(() => demoShiftControlStore.hasSlots)
  const meta = computed<Meta>(() => demoShiftControlStore.getMeta ?? DEFAULT_APP_TABLE_META)

  const isShowStatusBadge = (slot: DemoSlot) => !!(slot.booking && !slot.booking.is_finished)

  const formatTime = (value: string | null): string => value ? dayjs(value).format(DateFormat.ExtendedTime) : ''

  const isCheckInLate = (slot: DemoSlot): boolean => {
    if (!slot.checked_in_at) return false
    return dayjs(slot.checked_in_at).isAfter(dayjs(slot.start))
  }

  const getTutorHref = (tutorId: number): string => useAdminRoutes('admin.tutor.show', { tutor_id: tutorId }) || ''

  const getBookingHref = (disciplineId: number, bookingId: number): string =>
    resolveUrl(AppRoute.LessonsEdit, { id: disciplineId, lessonId: bookingId }) ?? ''

  const setChangePage = async (value: number): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        current_page: String(value),
      },
    })

    await useSlotsTrackerApi()
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
      },
    })

    await useSlotsTrackerApi()
  }

  return {
    slots,
    isLoading,
    hasSlots,
    meta,
    formatTime,
    isCheckInLate,
    getTutorHref,
    getBookingHref,
    setChangePage,
    setChangePerPage,
    isShowStatusBadge,
  }
}
